import React from "react";
import { useBrand } from "@/context/BrandContext";
import { BRANDS, brandUrl } from "@/lib/brands";
import { MapPin, Phone, Mail, Clock, ArrowUpRight } from "lucide-react";

const LocationCard = ({ data, isActive }) => (
  <div
    className={`relative p-8 md:p-10 border transition-colors ${
      isActive
        ? "border-black bg-white"
        : "border-black/15 bg-[color:var(--bg-alt)] hover:bg-white"
    }`}
    data-testid={`location-card-${data.slug}`}
  >
    <div className="flex items-start justify-between mb-6">
      <div>
        <div className="eyebrow text-[color:var(--primary)] mb-2">
          {isActive ? "Viewing This Yard" : "Sister Yard"}
        </div>
        <h3 className="font-display text-3xl md:text-4xl uppercase leading-none">
          {data.short_name}
        </h3>
        <div className="font-mono text-xs uppercase tracking-widest text-black/50 mt-2">
          {data.city}, {data.state}
        </div>
      </div>
      {isActive && (
        <span className="font-mono text-[10px] uppercase tracking-widest bg-[color:var(--primary)] text-white px-2 py-1">
          Active
        </span>
      )}
    </div>

    <ul className="space-y-3 text-sm mb-8">
      <li className="flex gap-3">
        <MapPin size={14} className="shrink-0 mt-0.5 text-[color:var(--accent)]" />
        <a href={data.map_url} target="_blank" rel="noreferrer">
          {data.address}
        </a>
      </li>
      <li className="flex gap-3">
        <Phone size={14} className="shrink-0 mt-0.5 text-[color:var(--accent)]" />
        <a href={`tel:${data.phone.replace(/[^0-9]/g, "")}`}>{data.phone}</a>
      </li>
      <li className="flex gap-3">
        <Mail size={14} className="shrink-0 mt-0.5 text-[color:var(--accent)]" />
        <a href={`mailto:${data.email}`}>{data.email}</a>
      </li>
    </ul>

    <div className="border-t border-black/10 pt-5 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Clock size={12} className="text-[color:var(--primary)]" />
        <span className="eyebrow">Hours</span>
      </div>
      <div className="space-y-1 text-xs font-mono">
        {data.hours.map((h) => (
          <div key={h.day} className="flex justify-between text-black/70">
            <span>{h.day}</span>
            <span>{h.hours}</span>
          </div>
        ))}
      </div>
    </div>

    <a
      href={brandUrl(data.slug)}
      className={isActive ? "hard-btn-ghost" : "hard-btn-primary"}
      data-testid={`visit-${data.slug}-btn`}
    >
      {isActive ? "You Are Here" : `Visit ${data.short_name}`}
      <ArrowUpRight size={14} />
    </a>
  </div>
);

const Locations = () => {
  const { brand } = useBrand();
  return (
    <section
      id="locations"
      className="section-pad bg-[color:var(--bg-alt)]"
      data-testid="locations-section"
    >
      <div className="container-x">
        <div className="mb-16 max-w-3xl">
          <div className="eyebrow text-[color:var(--primary)] mb-4">
            ◆ Locations
          </div>
          <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] tracking-tighter">
            Two Yards. <br />
            One Family.
          </h2>
          <p className="mt-6 text-black/70 leading-relaxed">
            Whether you're bringing a pickup load or a 40-yard roll-off, one of
            our yards is within driving distance. Both are run by the same
            team, same values, same fair pricing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.values(BRANDS).map((b) => (
            <LocationCard key={b.slug} data={b} isActive={brand.slug === b.slug} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Locations;
